
import { useState, useEffect, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { checkAdminStatus } from "@/utils/auth-utils";

export const useAuthState = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Force a refresh of the auth state - useful when components need to explicitly check auth
  const forceRefresh = useCallback(async () => {
    console.log('Force refreshing auth state');
    try {
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error during force refresh:', error);
        setSession(null);
        setUser(null);
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }
      
      const currentSession = data.session;
      console.log('Force refresh session result:', currentSession?.user?.id);
      
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (currentSession?.user) {
        const adminStatus = await checkAdminStatus(currentSession.user.id);
        setIsAdmin(adminStatus);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Unexpected error during force refresh:', error);
      setSession(null);
      setUser(null);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize auth state and set up listeners
  useEffect(() => {
    let isMounted = true;
    console.log('Auth state provider initializing...');
    
    // Set up the auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        
        if (!isMounted) {
          console.log('Auth state changed but component unmounted, ignoring');
          return;
        }
        
        if (event === 'SIGNED_OUT') {
          console.log('User signed out, clearing state');
          // Clear everything on sign out
          setSession(null);
          setUser(null);
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          try {
            const adminStatus = await checkAdminStatus(session.user.id);
            if (isMounted) {
              console.log('Setting admin status:', adminStatus);
              setIsAdmin(adminStatus);
            }
          } catch (error) {
            console.error('Error checking admin status:', error);
          }
        }
        
        if (isMounted) {
          console.log('Auth state change completed, setting isLoading to false');
          setIsLoading(false);
          setAuthInitialized(true);
        }
      }
    );

    // Then check for existing session
    const initializeAuth = async () => {
      try {
        console.log('Getting initial session...');
        const { data, error } = await supabase.auth.getSession();
        
        if (!isMounted) {
          console.log('Initial session retrieved but component unmounted, ignoring');
          return;
        }
        
        if (error) {
          console.error('Error getting initial session:', error);
          if (isMounted) {
            setIsLoading(false);
            setAuthInitialized(true);
          }
          return;
        }
        
        const currentSession = data.session;
        console.log('Initial session retrieved:', currentSession?.user?.id);
        
        if (isMounted) {
          setSession(currentSession);
          
          if (currentSession?.user) {
            setUser(currentSession.user);
            try {
              const adminStatus = await checkAdminStatus(currentSession.user.id);
              if (isMounted) {
                console.log('Setting initial admin status:', adminStatus);
                setIsAdmin(adminStatus);
              }
            } catch (error) {
              console.error('Error checking admin status during initialization:', error);
            }
          }
        }
      } catch (error) {
        console.error('Unexpected error during auth initialization:', error);
      } finally {
        if (isMounted) {
          console.log('Auth initialization completed, setting isLoading to false');
          setIsLoading(false);
          setAuthInitialized(true);
        }
      }
    };

    initializeAuth();

    return () => {
      // Clean up subscription and prevent state updates after unmount
      isMounted = false;
      subscription.unsubscribe();
      console.log('Auth state provider cleanup completed');
    };
  }, []);

  // Debug output for key state changes
  useEffect(() => {
    console.log('Auth state updated:', { 
      session: session?.user?.id ? 'present' : 'null', 
      user: user?.id,
      isLoading, 
      isAdmin,
      authInitialized
    });
  }, [session, user, isLoading, isAdmin, authInitialized]);

  return {
    session,
    user,
    isLoading,
    isAdmin,
    authInitialized,
    forceRefresh,
  };
};
